import {HttpClient} from '@angular/common/http';
import {EventEmitter, Injectable, Output} from '@angular/core';
import {Observable} from 'rxjs';
import {
    AffiliateBalanceListMxObj,
    AffiliateBalanceMxObj,
    EditPaymentAccountPrlObj,
    IdNameMxObj,
    NewPaymentAccountPrlObj,
    UpdatePaymentAccountPrlObj,
    ValidationResponseObj
} from 'mx-core';
import {PaymentRequestPropertiesPrlObj} from '../../../../../generated/generated.model';

const FINANCE_REST_URL: string = '/rest/finance/';

@Injectable({
    providedIn: 'root'
})
export class PrlFinanceService {

    @Output() closePaymentRequest: EventEmitter<void> = new EventEmitter<void>();

    private selectedMerchant: AffiliateBalanceMxObj = null;
    private selectedMerchantProperties: PaymentRequestPropertiesPrlObj = null;
    private newPaymentRequest: boolean = false;
    private totalPaymentRequestProperties: PaymentRequestPropertiesPrlObj = null;
    private totalBalance: AffiliateBalanceMxObj = null;

    constructor(
        private httpClient: HttpClient
    ) {
    }

    public getSelectedMerchant = (): AffiliateBalanceMxObj => {
        return this.selectedMerchant;
    };

    public setSelectedMerchant = (merchant: AffiliateBalanceMxObj) => {
        return this.selectedMerchant = merchant;
    };

    public getSelectedMerchantProperties = (): PaymentRequestPropertiesPrlObj => {
        return this.selectedMerchantProperties;
    };

    public setSelectedMerchantProperties = (properties: PaymentRequestPropertiesPrlObj) => {
        return this.selectedMerchantProperties = properties;
    };

    public getTotalPaymentRequestProperties = (): PaymentRequestPropertiesPrlObj => {
        return this.totalPaymentRequestProperties;
    };

    public setTotalPaymentRequestProperties = (properties: PaymentRequestPropertiesPrlObj) => {
        return this.totalPaymentRequestProperties = properties;
    };

    public isNewPaymentRequest = (): boolean => {
        return this.newPaymentRequest;
    };

    public setNewPaymentRequest = (value: boolean): void => {
        this.newPaymentRequest = value;
    };

    public getTotalBalance = (): AffiliateBalanceMxObj => {
        return this.totalBalance;
    };

    public setTotalBalance = (value: AffiliateBalanceMxObj): void => {
        this.totalBalance = value;
    };

    public getDashboardBalance(merchantId?: number): Observable<AffiliateBalanceMxObj> {
        let url = FINANCE_REST_URL + 'balance';
        if (merchantId) {
            url += '/merchant/' + merchantId;
        }
        return this.httpClient.get<AffiliateBalanceMxObj>(url);
    };

    public showPaymentAccounts(): Observable<boolean> {
        return this.httpClient.get<boolean>(FINANCE_REST_URL + 'show-payment-accounts');
    };

    public createWithdrawal(newWithdrawalObj): Observable<object> {
        return this.httpClient.put(FINANCE_REST_URL + 'create-withdrawal', newWithdrawalObj);
    };

    public getPaymentRequestProperties(merchantId?: number): Observable<PaymentRequestPropertiesPrlObj> {
        let url = FINANCE_REST_URL + 'payment-request-properties';

        if (merchantId) {
            url += '/merchant/' + merchantId;
        }

        return this.httpClient.get<PaymentRequestPropertiesPrlObj>(url);
    };

    public addPaymentAccount(paymentData): Observable<object> {
        return this.httpClient.post(FINANCE_REST_URL + 'payment-accounts', paymentData);
    };

    public modifyPaymentAccount(paymentAccoutnData): Observable<object> {
        return this.httpClient.put(FINANCE_REST_URL + 'payment-accounts', paymentAccoutnData);
    };

    public updateSecureIdField(paymentAccountData): Observable<object> {
        return this.httpClient.post(FINANCE_REST_URL + 'update-secure-id-field', paymentAccountData);
    };

    public isBalancePerMerchant(): Observable<boolean> {
        return this.httpClient.get<boolean>(FINANCE_REST_URL + 'is-balance-per-merchant');
    };

    public isAffiliatePayable(): Observable<boolean> {
        return this.httpClient.get<boolean>(FINANCE_REST_URL + 'is-affiliate-payable');
    };

    public getAffiliateId(): Observable<string> {
        return this.httpClient.get(FINANCE_REST_URL + 'get-affiliate-id', {
            responseType: 'text'
        });
    }

    public getMerchants(): Observable<Array<IdNameMxObj>> {
        return this.httpClient.get<Array<IdNameMxObj>>(FINANCE_REST_URL + 'merchants');
    };

    public getBalancesPerMerchant(): Observable<AffiliateBalanceListMxObj> {
        return this.httpClient.get<AffiliateBalanceListMxObj>(FINANCE_REST_URL + 'balances');
    }

    public getPaymentMethods(): Observable<Array<IdNameMxObj>> {
        return this.httpClient.get<Array<IdNameMxObj>>(FINANCE_REST_URL + 'get-payment-methods');
    }

    public savePaymentAccount(newPaymentAccountPrlObj: NewPaymentAccountPrlObj): Observable<object> {
        return this.httpClient.post(FINANCE_REST_URL + 'payment-accounts/account-data/new', newPaymentAccountPrlObj);
    }

    public updatePaymentAccount(paymentAccountId: number, updatePaymentAccountPrlObj: UpdatePaymentAccountPrlObj): Observable<object> {
        return this.httpClient.put(FINANCE_REST_URL + 'payment-accounts/account-data/' + + paymentAccountId, updatePaymentAccountPrlObj);
    }

    public validate(paymentAccountData: NewPaymentAccountPrlObj | EditPaymentAccountPrlObj, url?: string): Observable<ValidationResponseObj | null> {
        return this.httpClient.post<null | ValidationResponseObj>(FINANCE_REST_URL + (url || 'payment-accounts/account-data/new/validate'), paymentAccountData);
    }

    getPaymentAccount(paymentAccountId: number): Observable<Record<string, string> | null> {
        return this.httpClient.get<any>(FINANCE_REST_URL + 'payment-accounts/account-data/' + paymentAccountId);
    }
}
